import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe } from 'lucide-react';
import {
  connectTelegram,
  createSite,
  deleteSite,
  disconnectTelegram,
  listSites,
  updateSite,
  type OfficeDay,
  type PrechatConfig,
  type PrechatField,
  type Site,
} from '../api/sites';
import { listDepartments } from '../api/departments';
import { getApiClient } from '../api/factory';
import { useAuthStore } from '../store/auth';
import { ApiError } from '../api/client';
import { useT } from '../i18n';
import { PageContainer, PageHeader } from '../components/Page';
import { EmptyState } from '../components/EmptyState';
import { SkeletonList } from '../components/Skeleton';

export function SitesPage() {
  const session = useAuthStore((s) => s.session);
  const isAdmin = session?.operator.role === 'admin';
  const queryClient = useQueryClient();
  const t = useT();
  const [showCreate, setShowCreate] = useState(false);

  const { data: sites, isLoading, error } = useQuery({
    queryKey: ['sites'] as const,
    queryFn: () => listSites(getApiClient()),
  });

  const createMutation = useMutation({
    mutationFn: (input: { name: string; domain?: string }) =>
      createSite(getApiClient(), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      setShowCreate(false);
    },
  });

  return (
    <PageContainer>
      <PageHeader
        title={t('sites.title')}
        action={
          isAdmin && (
            <button
              type="button"
              onClick={() => setShowCreate((v) => !v)}
              className="rounded-md bg-accent-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-accent-700"
            >
              {t('sites.create')}
            </button>
          )
        }
      />

      {isAdmin && showCreate && (
        <CreateSiteForm
          loading={createMutation.isPending}
          error={createMutation.error instanceof ApiError ? createMutation.error.message : null}
          onSubmit={(input) => createMutation.mutate(input)}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {isLoading && <SkeletonList ariaLabel={t('common.loading')} />}
      {error instanceof ApiError && (
        <p className="text-sm text-red-600" role="alert">
          {error.message}
        </p>
      )}

      {sites && sites.length === 0 && (
        <EmptyState
          icon={Globe}
          title={t('sites.empty')}
          {...(isAdmin
            ? {
                actionLabel: t('sites.create'),
                onAction: () => setShowCreate(true),
              }
            : {})}
        />
      )}

      {sites && sites.length > 0 && (
        <ul className="space-y-3">
          {sites.map((s) => (
            <SiteRow key={s.id} site={s} canEdit={isAdmin} />
          ))}
        </ul>
      )}
    </PageContainer>
  );
}

function CreateSiteForm({
  onSubmit,
  loading,
  error,
  onCancel,
}: {
  onSubmit: (input: { name: string; domain?: string }) => void;
  loading: boolean;
  error: string | null;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const t = useT();

  function handle(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (name.trim() === '') return;
    const input: { name: string; domain?: string } = { name: name.trim() };
    if (domain.trim() !== '') input.domain = domain.trim();
    onSubmit(input);
    setName('');
    setDomain('');
  }

  return (
    <form
      onSubmit={handle}
      className="mb-4 flex items-end gap-3 rounded-md border border-slate-200 bg-white p-4 shadow-card"
    >
      <label className="flex-1">
        <span className="mb-1 block text-xs text-slate-500">{t('sites.name')}</span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
          placeholder="acme.com"
        />
      </label>
      <label className="flex-1">
        <span className="mb-1 block text-xs text-slate-500">{t('sites.domainOptional')}</span>
        <input
          type="text"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
          placeholder="example.com"
        />
      </label>
      <button
        type="submit"
        disabled={loading || name.trim() === ''}
        className="rounded-md bg-accent-600 px-3.5 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:bg-slate-300"
      >
        {t('sites.create')}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="rounded-md px-3 py-1.5 text-sm text-slate-500 hover:text-slate-900"
      >
        {t('sites.cancel')}
      </button>
      {error && <span className="text-xs text-red-600">{error}</span>}
    </form>
  );
}

function SiteRow({ site, canEdit }: { site: Site; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(site.name);
  const [domain, setDomain] = useState(site.domain ?? '');
  const [copied, setCopied] = useState(false);
  const t = useT();

  const update = useMutation({
    mutationFn: () =>
      updateSite(getApiClient(), site.id, {
        name,
        domain: domain.trim() === '' ? null : domain.trim(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      setEditing(false);
    },
  });
  const remove = useMutation({
    mutationFn: () => deleteSite(getApiClient(), site.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sites'] }),
  });
  const toggleStatus = useMutation({
    mutationFn: () =>
      updateSite(getApiClient(), site.id, {
        status: site.status === 'active' ? 'paused' : 'active',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sites'] }),
  });

  async function copyKey() {
    try {
      await navigator.clipboard.writeText(site.widgetKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }

  return (
    <li className="rounded-md border border-slate-200 bg-white p-4 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="flex gap-2">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex-1 rounded-md border border-slate-200 px-2 py-1 text-sm focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
              />
              <input
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="domain"
                className="flex-1 rounded-md border border-slate-200 px-2 py-1 text-sm focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
              />
            </div>
          ) : (
            <>
              <div className="font-medium text-slate-900">{site.name}</div>
              <div className="truncate text-xs text-slate-500">{site.domain ?? '—'}</div>
            </>
          )}
        </div>
        <StatusPill active={site.status === 'active'} label={site.status} />
      </div>
      <div className="mt-3 flex items-center gap-2 text-xs">
        <code className="rounded bg-slate-50 px-2 py-1 font-mono text-slate-700">
          {site.widgetKey}
        </code>
        <button
          type="button"
          onClick={copyKey}
          className="text-accent-600 hover:text-accent-700"
        >
          {copied ? t('sites.copied') : t('sites.copyKey')}
        </button>
      </div>
      {/* ANX-067 — детект установки виджета (heartbeat от widget.js). */}
      <div className="mt-2 flex items-center gap-1.5 text-xs">
        <span
          aria-hidden="true"
          className={
            site.widgetLastSeenAt
              ? 'h-2 w-2 shrink-0 rounded-full bg-emerald-500'
              : 'h-2 w-2 shrink-0 rounded-full bg-slate-300'
          }
        />
        {site.widgetLastSeenAt ? (
          <span className="text-slate-600">
            {site.widgetSeenDomain
              ? t('sites.widget.detectedOn', { domain: site.widgetSeenDomain })
              : t('sites.widget.detected')}
          </span>
        ) : (
          <span className="text-slate-400">{t('sites.widget.notDetected')}</span>
        )}
      </div>
      {canEdit && (
        <div className="mt-3 flex gap-3 text-xs">
          {editing ? (
            <>
              <button
                type="button"
                disabled={update.isPending}
                onClick={() => update.mutate()}
                className="rounded-md bg-accent-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-accent-700 disabled:bg-slate-300"
              >
                {t('sites.save')}
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="text-slate-500 hover:text-slate-900"
              >
                {t('sites.cancel')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-slate-600 hover:text-slate-900"
              >
                {t('sites.edit')}
              </button>
              <button
                type="button"
                onClick={() => toggleStatus.mutate()}
                className="text-slate-600 hover:text-slate-900"
              >
                {site.status === 'active' ? t('sites.pause') : t('sites.resume')}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (confirm(t('sites.confirmDelete', { name: site.name }))) remove.mutate();
                }}
                className="text-red-600 hover:text-red-700"
              >
                {t('sites.delete')}
              </button>
            </>
          )}
        </div>
      )}
      {canEdit && <WidgetThemeBlock site={site} />}
      {canEdit && <EmailTranscriptBlock site={site} />}
      {canEdit && <ProactiveBlock site={site} />}
      {canEdit && <OfficeHoursBlock site={site} />}
      {canEdit && <PreChatBlock site={site} />}
      {canEdit && <FollowUpEmailBlock site={site} />}
      {canEdit && <TelegramBlock site={site} />}
    </li>
  );
}

// DAY_KEYS — порядок дней недели в UI часов работы (ANX-061).
const OFFICE_DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

/**
 * OfficeHoursBlock — ANX-061. Admin задаёт расписание работы по дням +
 * таймзону сайта и offline-сообщение. Вне рабочих часов виджет показывает
 * offline-плашку и собирает email. Само «сейчас рабочее время» вычисляет
 * виджет по timezone — backend только хранит конфиг.
 */
export function OfficeHoursBlock({ site }: { site: Site }) {
  const t = useT();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(site.officeHoursEnabled);
  const [timezone, setTimezone] = useState(site.officeTimezone);
  const [collectEmail, setCollectEmail] = useState(site.offlineCollectEmail);
  const [offlineMessage, setOfflineMessage] = useState(site.offlineMessage);
  const [days, setDays] = useState<Record<string, OfficeDay>>(() => ({ ...site.officeHours }));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      updateSite(getApiClient(), site.id, {
        officeHoursEnabled: enabled,
        officeTimezone: timezone,
        officeHours: days,
        offlineMessage,
        offlineCollectEmail: collectEmail,
      }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
    onError: (e: unknown) => {
      setSaved(false);
      setError(e instanceof ApiError ? e.message : t('sites.saveError'));
    },
  });

  function toggleDay(key: string, on: boolean): void {
    setDays((prev) => {
      const next = { ...prev };
      if (on) next[key] = prev[key] ?? { start: '09:00', end: '18:00' };
      else delete next[key];
      return next;
    });
  }

  function setDayField(key: string, field: 'start' | 'end', value: string): void {
    setDays((prev) => {
      const base = prev[key] ?? { start: '09:00', end: '18:00' };
      return { ...prev, [key]: { ...base, [field]: value } };
    });
  }

  function submit(ev: FormEvent): void {
    ev.preventDefault();
    setSaved(false);
    if (timezone.trim() === '') {
      setError(t('sites.office.tzError'));
      return;
    }
    for (const key of OFFICE_DAY_KEYS) {
      const d = days[key];
      if (d && d.start >= d.end) {
        setError(t('sites.office.intervalError'));
        return;
      }
    }
    if (offlineMessage.length > 280) {
      setError(t('sites.office.messageError'));
      return;
    }
    save.mutate();
  }

  return (
    <form
      onSubmit={submit}
      noValidate
      className="mt-3 border-t border-slate-100 pt-3 text-sm"
      aria-label={t('sites.office.title')}
    >
      <div className="mb-2 font-medium text-slate-900">{t('sites.office.title')}</div>
      <p className="mb-2 text-xs text-slate-500">{t('sites.office.hint')}</p>
      <label className="mb-2 flex items-start gap-2">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.currentTarget.checked)}
          className="mt-0.5"
        />
        <span>{t('sites.office.enabledLabel')}</span>
      </label>
      <fieldset disabled={!enabled} className="grid gap-2">
        <label className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
          <span className="text-xs text-slate-600 sm:w-40">{t('sites.office.tzLabel')}</span>
          <input
            type="text"
            value={timezone}
            placeholder="Europe/Moscow"
            onChange={(e) => setTimezone(e.currentTarget.value)}
            className="w-56 rounded border border-slate-300 px-2 py-1 disabled:bg-slate-100"
          />
        </label>
        <div className="mt-1 flex flex-col gap-1">
          {OFFICE_DAY_KEYS.map((key) => {
            const d = days[key];
            const on = d !== undefined;
            return (
              <div key={key} className="flex items-center gap-2">
                <label className="flex w-28 items-center gap-2">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={(e) => toggleDay(key, e.currentTarget.checked)}
                  />
                  <span className="text-xs text-slate-700">{t(`sites.office.day.${key}`)}</span>
                </label>
                <input
                  type="time"
                  value={d?.start ?? '09:00'}
                  disabled={!on}
                  onChange={(e) => setDayField(key, 'start', e.currentTarget.value)}
                  className="rounded border border-slate-300 px-2 py-1 text-xs disabled:bg-slate-100"
                  aria-label={`${t(`sites.office.day.${key}`)} ${t('sites.office.fromLabel')}`}
                />
                <span className="text-xs text-slate-400">—</span>
                <input
                  type="time"
                  value={d?.end ?? '18:00'}
                  disabled={!on}
                  onChange={(e) => setDayField(key, 'end', e.currentTarget.value)}
                  className="rounded border border-slate-300 px-2 py-1 text-xs disabled:bg-slate-100"
                  aria-label={`${t(`sites.office.day.${key}`)} ${t('sites.office.toLabel')}`}
                />
              </div>
            );
          })}
        </div>
        <label className="mt-1 flex flex-col gap-1">
          <span className="text-xs text-slate-600">{t('sites.office.messageLabel')}</span>
          <textarea
            rows={2}
            maxLength={280}
            placeholder={t('sites.office.messagePlaceholder')}
            value={offlineMessage}
            onChange={(e) => setOfflineMessage(e.currentTarget.value)}
            className="rounded border border-slate-300 px-2 py-1 disabled:bg-slate-100"
          />
          <span className="text-xs text-slate-400">{`${offlineMessage.length}/280`}</span>
        </label>
        <label className="mt-1 flex items-start gap-2">
          <input
            type="checkbox"
            checked={collectEmail}
            onChange={(e) => setCollectEmail(e.currentTarget.checked)}
            className="mt-0.5"
          />
          <span className="text-xs text-slate-700">{t('sites.office.collectEmailLabel')}</span>
        </label>
      </fieldset>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="submit"
          disabled={save.isPending}
          className="rounded bg-blue-600 px-3 py-1 text-white text-xs hover:bg-blue-700 disabled:opacity-50"
        >
          {save.isPending ? t('sites.saving') : t('sites.save')}
        </button>
        {saved && <span className="text-xs text-emerald-600">{t('sites.saved')}</span>}
        {error && <span role="alert" className="text-xs text-red-600">{error}</span>}
      </div>
    </form>
  );
}

/**
 * PreChatBlock — ANX-072. Admin настраивает pre-chat форму перед началом чата:
 * какие поля собирать (имя/email/телефон/вопрос) и обязательны ли, нужен ли
 * выбор отдела. Выбор отдела можно включить ТОЛЬКО если у организации есть
 * отделы — иначе тумблер деактивирован и ссылается на их отсутствие (отделы
 * создаются в Настройках, секция «Отделы»).
 */
export function PreChatBlock({ site }: { site: Site }) {
  const t = useT();
  const queryClient = useQueryClient();
  const { data: departments } = useQuery({
    queryKey: ['departments'] as const,
    queryFn: () => listDepartments(getApiClient()),
  });
  const hasDepartments = (departments?.length ?? 0) > 0;

  const [enabled, setEnabled] = useState(site.prechatEnabled);
  const [cfg, setCfg] = useState<PrechatConfig>(() => structuredCloneConfig(site.prechatConfig));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      updateSite(getApiClient(), site.id, { prechatEnabled: enabled, prechatConfig: cfg }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
    onError: (e: unknown) => {
      setSaved(false);
      setError(e instanceof ApiError ? e.message : t('sites.saveError'));
    },
  });

  function setField(key: 'name' | 'email' | 'phone', patch: Partial<PrechatField>): void {
    setCfg((p) => ({ ...p, fields: { ...p.fields, [key]: normalizeField({ ...p.fields[key], ...patch }) } }));
  }
  function setQuestion(patch: Partial<PrechatField>): void {
    setCfg((p) => ({ ...p, question: normalizeField({ ...p.question, ...patch }) }));
  }
  function setDepartment(patch: Partial<PrechatField>): void {
    setCfg((p) => ({ ...p, department: normalizeField({ ...p.department, ...patch }) }));
  }

  function submit(ev: FormEvent): void {
    ev.preventDefault();
    setSaved(false);
    if (cfg.intro.length > 280) {
      setError(t('sites.prechat.introError'));
      return;
    }
    save.mutate();
  }

  return (
    <form
      onSubmit={submit}
      noValidate
      className="mt-3 border-t border-slate-100 pt-3 text-sm"
      aria-label={t('sites.prechat.title')}
    >
      <div className="mb-2 font-medium text-slate-900">{t('sites.prechat.title')}</div>
      <p className="mb-2 text-xs text-slate-500">{t('sites.prechat.hint')}</p>
      <label className="mb-2 flex items-start gap-2">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.currentTarget.checked)}
          className="mt-0.5"
        />
        <span>{t('sites.prechat.enabledLabel')}</span>
      </label>
      <fieldset disabled={!enabled} className="grid gap-2">
        {(['name', 'email', 'phone'] as const).map((key) => (
          <FieldRow
            key={key}
            label={t(`sites.prechat.field.${key}`)}
            field={cfg.fields[key]}
            onChange={(patch) => setField(key, patch)}
            t={t}
          />
        ))}
        <FieldRow
          label={t('sites.prechat.field.question')}
          field={cfg.question}
          onChange={setQuestion}
          t={t}
        />

        {/* Выбор отдела: деактивирован, если нет отделов организации. */}
        <div className="mt-1 border-t border-slate-100 pt-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={cfg.department.enabled}
              disabled={!hasDepartments}
              onChange={(e) => setDepartment({ enabled: e.currentTarget.checked })}
            />
            <span className={hasDepartments ? '' : 'text-slate-400'}>
              {t('sites.prechat.departmentLabel')}
            </span>
          </label>
          {!hasDepartments && (
            <p className="ml-6 mt-0.5 text-xs text-amber-600">{t('sites.prechat.noDepartments')}</p>
          )}
          {hasDepartments && cfg.department.enabled && (
            <label className="ml-6 mt-1 flex items-center gap-2">
              <input
                type="checkbox"
                checked={cfg.department.required}
                onChange={(e) => setDepartment({ required: e.currentTarget.checked })}
              />
              <span className="text-xs text-slate-600">{t('sites.prechat.requiredLabel')}</span>
            </label>
          )}
        </div>

        <label className="mt-1 flex flex-col gap-1">
          <span className="text-xs text-slate-600">{t('sites.prechat.introLabel')}</span>
          <textarea
            rows={2}
            maxLength={280}
            placeholder={t('sites.prechat.introPlaceholder')}
            value={cfg.intro}
            onChange={(e) => setCfg((p) => ({ ...p, intro: e.currentTarget.value }))}
            className="rounded border border-slate-300 px-2 py-1 disabled:bg-slate-100"
          />
          <span className="text-xs text-slate-400">{`${cfg.intro.length}/280`}</span>
        </label>
      </fieldset>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="submit"
          disabled={save.isPending}
          className="rounded bg-blue-600 px-3 py-1 text-white text-xs hover:bg-blue-700 disabled:opacity-50"
        >
          {save.isPending ? t('sites.saving') : t('sites.save')}
        </button>
        {saved && <span className="text-xs text-emerald-600">{t('sites.saved')}</span>}
        {error && <span role="alert" className="text-xs text-red-600">{error}</span>}
      </div>
    </form>
  );
}

/** FieldRow — одна строка конфига поля: вкл + обязательность. */
function FieldRow({
  label,
  field,
  onChange,
  t,
}: {
  label: string;
  field: PrechatField;
  onChange: (patch: Partial<PrechatField>) => void;
  t: ReturnType<typeof useT>;
}) {
  return (
    <div className="flex items-center gap-4">
      <label className="flex w-40 items-center gap-2">
        <input
          type="checkbox"
          checked={field.enabled}
          onChange={(e) => onChange({ enabled: e.currentTarget.checked })}
        />
        <span className="text-xs text-slate-700">{label}</span>
      </label>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={field.required}
          disabled={!field.enabled}
          onChange={(e) => onChange({ required: e.currentTarget.checked })}
        />
        <span className="text-xs text-slate-500">{t('sites.prechat.requiredLabel')}</span>
      </label>
    </div>
  );
}

// normalizeField — required ⇒ enabled (нельзя требовать скрытое поле); снятие
// enabled снимает и required.
function normalizeField(f: PrechatField): PrechatField {
  if (f.required) return { enabled: true, required: true };
  return { enabled: f.enabled, required: false };
}

function structuredCloneConfig(c: PrechatConfig): PrechatConfig {
  return {
    fields: { name: { ...c.fields.name }, email: { ...c.fields.email }, phone: { ...c.fields.phone } },
    question: { ...c.question },
    department: { ...c.department },
    intro: c.intro,
  };
}

/**
 * ProactiveBlock — ANX-051. Admin задаёт автозапуск виджета через N секунд
 * на странице. Welcome-сообщение появляется как первое operator-style
 * сообщение в чате (визуально без сохранения в БД). По умолчанию выключено
 * на mobile — full-screen виджет всплывает поверх контента и раздражает.
 *
 * Границы валидации совпадают с backend (5..3600 сек, ≤280 символов).
 */
export function ProactiveBlock({ site }: { site: Site }) {
  const t = useT();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(site.proactiveEnabled);
  const [delaySec, setDelaySec] = useState<number>(site.proactiveDelaySec);
  const [message, setMessage] = useState(site.proactiveMessage);
  const [mobileEnabled, setMobileEnabled] = useState(site.proactiveMobileEnabled);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      updateSite(getApiClient(), site.id, {
        proactiveEnabled: enabled,
        proactiveDelaySec: delaySec,
        proactiveMessage: message,
        proactiveMobileEnabled: mobileEnabled,
      }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
    onError: (e: unknown) => {
      setSaved(false);
      setError(e instanceof ApiError ? e.message : t('sites.saveError'));
    },
  });

  function submit(ev: FormEvent) {
    ev.preventDefault();
    setSaved(false);
    if (delaySec < 5 || delaySec > 3600) {
      setError(t('sites.proactive.delayError'));
      return;
    }
    if (message.length > 280) {
      setError(t('sites.proactive.messageError'));
      return;
    }
    save.mutate();
  }

  return (
    <form
      onSubmit={submit}
      noValidate
      className="mt-3 border-t border-slate-100 pt-3 text-sm"
      aria-label={t('sites.proactive.title')}
    >
      <div className="mb-2 font-medium text-slate-900">
        {t('sites.proactive.title')}
      </div>
      <p className="mb-2 text-xs text-slate-500">{t('sites.proactive.hint')}</p>
      <label className="mb-2 flex items-start gap-2">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.currentTarget.checked)}
          className="mt-0.5"
        />
        <span>{t('sites.proactive.enabledLabel')}</span>
      </label>
      <fieldset disabled={!enabled} className="grid gap-2 sm:grid-cols-[auto_1fr]">
        <label htmlFor={`proactive-delay-${site.id}`} className="self-center">
          {t('sites.proactive.delayLabel')}
        </label>
        <input
          id={`proactive-delay-${site.id}`}
          type="number"
          min={5}
          max={3600}
          step={5}
          value={delaySec}
          onChange={(e) => setDelaySec(Number(e.currentTarget.value))}
          className="w-32 rounded border border-slate-300 px-2 py-1 disabled:bg-slate-100"
        />
        <label htmlFor={`proactive-message-${site.id}`} className="self-start mt-1">
          {t('sites.proactive.messageLabel')}
        </label>
        <textarea
          id={`proactive-message-${site.id}`}
          rows={2}
          maxLength={280}
          placeholder={t('sites.proactive.messagePlaceholder')}
          value={message}
          onChange={(e) => setMessage(e.currentTarget.value)}
          className="rounded border border-slate-300 px-2 py-1 disabled:bg-slate-100"
        />
        <span />
        <span className="text-xs text-slate-400">
          {`${message.length}/280`}
        </span>
        <label className="col-span-2 mt-1 flex items-start gap-2">
          <input
            type="checkbox"
            checked={mobileEnabled}
            onChange={(e) => setMobileEnabled(e.currentTarget.checked)}
            className="mt-0.5"
          />
          <span className="text-xs text-slate-700">
            {t('sites.proactive.mobileLabel')}
          </span>
        </label>
      </fieldset>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="submit"
          disabled={save.isPending}
          className="rounded bg-blue-600 px-3 py-1 text-white text-xs hover:bg-blue-700 disabled:opacity-50"
        >
          {save.isPending ? t('sites.saving') : t('sites.save')}
        </button>
        {saved && <span className="text-xs text-emerald-600">{t('sites.saved')}</span>}
        {error && <span role="alert" className="text-xs text-red-600">{error}</span>}
      </div>
    </form>
  );
}

/**
 * EmailTranscriptBlock — ANX-050. Toggle для site.email_transcripts_enabled.
 * Admin-only (canEdit guard выше). Когда включён — виджет на сайте показывает
 * visitor'у чекбокс «прислать копию переписки на email».
 */
function EmailTranscriptBlock({ site }: { site: Site }) {
  const t = useT();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const toggle = useMutation({
    mutationFn: (next: boolean) =>
      updateSite(getApiClient(), site.id, { emailTranscriptsEnabled: next }),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : t('sites.saveError'));
    },
  });

  return (
    <div className="mt-3 border-t border-slate-100 pt-3 text-sm">
      <label className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={site.emailTranscriptsEnabled}
          disabled={toggle.isPending}
          onChange={(e) => toggle.mutate(e.currentTarget.checked)}
          className="mt-0.5"
        />
        <span>
          <span className="block font-medium text-slate-900">
            {t('sites.transcripts.title')}
          </span>
          <span className="block text-xs text-slate-500">
            {t('sites.transcripts.hint')}
          </span>
        </span>
      </label>
      {error && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * FollowUpEmailBlock — ANX-079. Toggle для site.follow_up_email_enabled.
 * Когда включён — если визитёр ушёл оффлайн, а оператор ответил, визитёру
 * (при наличии email + согласия) уходит follow-up письмо «вам ответили».
 */
export function FollowUpEmailBlock({ site }: { site: Site }) {
  const t = useT();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const toggle = useMutation({
    mutationFn: (next: boolean) =>
      updateSite(getApiClient(), site.id, { followUpEmailEnabled: next }),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : t('sites.saveError'));
    },
  });

  return (
    <div className="mt-3 border-t border-slate-100 pt-3 text-sm">
      <label className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={site.followUpEmailEnabled}
          disabled={toggle.isPending}
          onChange={(e) => toggle.mutate(e.currentTarget.checked)}
          className="mt-0.5"
        />
        <span>
          <span className="block font-medium text-slate-900">
            {t('sites.followUp.title')}
          </span>
          <span className="block text-xs text-slate-500">
            {t('sites.followUp.hint')}
          </span>
        </span>
      </label>
      {error && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * TelegramBlock — ANX-065. Подключение Telegram-бота к сайту: admin вставляет
 * токен от @BotFather, сервер регистрирует webhook и сохраняет токен.
 * Входящие из бота падают в operator-inbox как диалоги канала «telegram».
 */
export function TelegramBlock({ site }: { site: Site }) {
  const t = useT();
  const queryClient = useQueryClient();
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);

  const connect = useMutation({
    mutationFn: (tok: string) => connectTelegram(getApiClient(), site.id, tok),
    onSuccess: () => {
      setError(null);
      setToken('');
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : t('sites.saveError')),
  });
  const disconnect = useMutation({
    mutationFn: () => disconnectTelegram(getApiClient(), site.id),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : t('sites.saveError')),
  });

  const busy = connect.isPending || disconnect.isPending;

  return (
    <div className="mt-3 border-t border-slate-100 pt-3 text-sm">
      <div className="font-medium text-slate-900">{t('sites.telegram.title')}</div>
      <p className="mt-0.5 text-xs text-slate-500">{t('sites.telegram.hint')}</p>

      {site.telegramConnected ? (
        <div className="mt-2 flex items-center gap-3">
          <StatusPill active label={t('sites.telegram.connected')} />
          <button
            type="button"
            disabled={busy}
            onClick={() => disconnect.mutate()}
            className="text-xs text-red-600 hover:text-red-700 disabled:opacity-50"
          >
            {t('sites.telegram.disconnect')}
          </button>
        </div>
      ) : (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={token}
            disabled={busy}
            onChange={(e) => setToken(e.currentTarget.value)}
            placeholder={t('sites.telegram.tokenPlaceholder')}
            className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-1 text-sm focus:border-accent-500 focus:ring-accent-500"
          />
          <button
            type="button"
            disabled={busy || token.trim() === ''}
            onClick={() => connect.mutate(token.trim())}
            className="rounded-md bg-accent-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {connect.isPending ? t('sites.telegram.connecting') : t('sites.telegram.connect')}
          </button>
        </div>
      )}
      {error && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

function StatusPill({ active, label }: { active: boolean; label: string }) {
  return (
    <span
      className={
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ' +
        (active
          ? 'bg-emerald-50 text-emerald-700'
          : 'bg-amber-50 text-amber-700')
      }
    >
      <span
        className={
          'mr-1.5 h-1.5 w-1.5 rounded-full ' +
          (active ? 'bg-emerald-500' : 'bg-amber-500')
        }
        aria-hidden="true"
      />
      {label}
    </span>
  );
}

/**
 * WidgetThemeBlock — настройка темы виджета: цвет акцента, позиция,
 * приветствие. Изменения уходят PATCH /sites/{id} с полем widget_settings;
 * backend мержит с текущим состоянием (см. UpdateSite handler).
 */
function WidgetThemeBlock({ site }: { site: Site }) {
  const t = useT();
  const queryClient = useQueryClient();
  const [themeColor, setThemeColor] = useState(site.widgetSettings.theme_color);
  const [position, setPosition] = useState<'right' | 'left'>(site.widgetSettings.position);
  const [greeting, setGreeting] = useState(site.widgetSettings.greeting ?? '');
  const [error, setError] = useState<string | null>(null);

  const dirty =
    themeColor !== site.widgetSettings.theme_color ||
    position !== site.widgetSettings.position ||
    greeting !== (site.widgetSettings.greeting ?? '');

  const save = useMutation({
    mutationFn: () =>
      updateSite(getApiClient(), site.id, {
        widgetSettings: { theme_color: themeColor, position, greeting },
      }),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : t('sites.saveError'));
    },
  });

  return (
    <div className="mt-4 grid gap-3 border-t border-slate-100 pt-3 text-sm md:grid-cols-[auto_1fr_auto] md:items-end">
      <label className="flex items-center gap-2">
        <span className="text-xs text-slate-500">{t('sites.theme.color')}</span>
        <input
          type="color"
          value={themeColor}
          onChange={(e) => setThemeColor(e.target.value)}
          className="h-8 w-12 cursor-pointer rounded border border-slate-200"
          aria-label={t('sites.theme.color')}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-slate-500">{t('sites.theme.greeting')}</span>
        <input
          type="text"
          value={greeting}
          onChange={(e) => setGreeting(e.target.value)}
          maxLength={200}
          placeholder={t('sites.theme.greetingPlaceholder')}
          className="rounded-md border border-slate-200 px-2 py-1 text-sm focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
        />
      </label>
      <div className="flex flex-col gap-2">
        <div className="flex gap-1 text-xs">
          {(['right', 'left'] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPosition(p)}
              className={
                'rounded-md border px-2 py-1 ' +
                (position === p
                  ? 'border-accent-600 bg-accent-600 text-white'
                  : 'border-slate-200 bg-white text-slate-600 hover:text-slate-900')
              }
            >
              {p === 'right' ? t('sites.theme.right') : t('sites.theme.left')}
            </button>
          ))}
        </div>
        <button
          type="button"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
          className="rounded-md bg-accent-600 px-3 py-1 text-xs font-medium text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {save.isPending ? t('common.loading') : t('sites.theme.save')}
        </button>
      </div>
      {error && (
        <p role="alert" className="text-xs text-red-600 md:col-span-3">
          {error}
        </p>
      )}
    </div>
  );
}
